import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

interface ScrollRestorationOptions {
  /**
   * Force the page to always start at the top, regardless of saved position
   */
  forceTop?: boolean;
  /**
   * Delay before restoring scroll position (ms)
   */
  delay?: number;
  /**
   * Whether to automatically save scroll position
   */
  save?: boolean;
  /**
   * Whether to automatically restore scroll position
   */
  restore?: boolean;
}

interface ScrollPosition {
  x: number;
  y: number;
  timestamp: number;
}

// Global scroll restoration manager
class ScrollManager {
  private static instance: ScrollManager;
  private positions = new Map<string, ScrollPosition>();
  private throttleTimer: NodeJS.Timeout | null = null;
  private isInitialized = false;

  static getInstance(): ScrollManager {
    if (!ScrollManager.instance) {
      ScrollManager.instance = new ScrollManager();
    }
    return ScrollManager.instance;
  }

  private constructor() {
    this.initializeBrowserSettings();
  }

  private initializeBrowserSettings() {
    if (typeof window !== 'undefined' && !this.isInitialized) {
      // Disable browser's native scroll restoration to prevent conflicts
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
      
      // Load saved positions from sessionStorage
      this.loadPositions();
      
      // Save positions when page is about to unload
      window.addEventListener('beforeunload', this.savePositions.bind(this));
      
      // Handle visibility changes (mobile app switching)
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.savePositions();
        }
      });

      this.isInitialized = true;
    }
  }

  private getStorageKey(): string {
    return 'gemba-scroll-positions';
  }

  private loadPositions() {
    try {
      const stored = sessionStorage.getItem(this.getStorageKey());
      if (stored) {
        const data = JSON.parse(stored);
        this.positions = new Map(Object.entries(data).map(([key, value]) => [key, value as ScrollPosition]));
      }
    } catch (error) {
      console.warn('Failed to load scroll positions:', error);
      this.positions.clear();
    }
  }

  private savePositions() {
    try {
      const data = Object.fromEntries(this.positions);
      sessionStorage.setItem(this.getStorageKey(), JSON.stringify(data));
    } catch (error) {
      console.warn('Failed to save scroll positions:', error);
    }
  }

  savePosition(path: string, x: number = window.scrollX, y: number = window.scrollY) {
    this.positions.set(path, {
      x,
      y,
      timestamp: Date.now()
    });
    
    // Throttled save to sessionStorage
    if (this.throttleTimer) {
      clearTimeout(this.throttleTimer);
    }
    this.throttleTimer = setTimeout(() => {
      this.savePositions();
    }, 100);
  }

  getPosition(path: string): ScrollPosition | null {
    return this.positions.get(path) || null;
  }

  clearPosition(path: string) {
    this.positions.delete(path);
    this.savePositions();
  }

  clearAllPositions() {
    this.positions.clear();
    sessionStorage.removeItem(this.getStorageKey());
  }
}

/**
 * Enhanced hook for managing scroll restoration behavior on individual pages.
 * This provides both automatic and manual scroll restoration functionality.
 */
export const useScrollRestoration = (options: ScrollRestorationOptions = {}) => {
  const {
    forceTop = false,
    delay = 50,
    save = true,
    restore = true
  } = options;
  
  const location = useLocation();
  const scrollManager = ScrollManager.getInstance();
  const hasRestoredRef = useRef(false);
  const currentPath = location.pathname + location.search;

  // Save scroll position function
  const saveScrollPosition = useCallback((customPath?: string) => {
    const path = customPath || currentPath;
    scrollManager.savePosition(path);
  }, [currentPath, scrollManager]);

  // Restore scroll position function
  const restoreScrollPosition = useCallback((customPath?: string, customDelay?: number) => {
    const path = customPath || currentPath;
    const restoreDelay = customDelay ?? delay;
    
    const doRestore = () => {
      const position = scrollManager.getPosition(path);
      if (position && !forceTop) {
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          window.scrollTo({
            left: position.x,
            top: position.y,
            behavior: 'auto'
          });
        });
      } else if (forceTop) {
        requestAnimationFrame(() => {
          window.scrollTo({
            left: 0,
            top: 0,
            behavior: 'auto'
          });
        });
      }
    };

    if (restoreDelay > 0) {
      setTimeout(doRestore, restoreDelay);
    } else {
      doRestore();
    }
  }, [currentPath, delay, forceTop, scrollManager]);

  // Clear scroll position function
  const clearScrollPosition = useCallback((customPath?: string) => {
    const path = customPath || currentPath;
    scrollManager.clearPosition(path);
  }, [currentPath, scrollManager]);

  // Auto-save scroll position on scroll events
  useEffect(() => {
    if (!save) return;

    let throttleTimer: NodeJS.Timeout;
    
    const handleScroll = () => {
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
      throttleTimer = setTimeout(() => {
        saveScrollPosition();
      }, 100);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [save, saveScrollPosition]);

  // Auto-restore scroll position on route change
  useEffect(() => {
    if (!restore || hasRestoredRef.current) return;
    
    // Mark as restored to prevent multiple restorations
    hasRestoredRef.current = true;
    
    // Reset the flag when location changes
    const timer = setTimeout(() => {
      hasRestoredRef.current = false;
    }, 1000);

    restoreScrollPosition();

    return () => clearTimeout(timer);
  }, [currentPath, restore, restoreScrollPosition]);

  // Save position before leaving the page
  useEffect(() => {
    if (!save) return;

    return () => {
      saveScrollPosition();
    };
  }, [currentPath, save, saveScrollPosition]);

  // Reset restored flag when path changes
  useEffect(() => {
    hasRestoredRef.current = false;
  }, [currentPath]);

  return {
    saveScrollPosition,
    restoreScrollPosition,
    clearScrollPosition,
    clearAllPositions: scrollManager.clearAllPositions.bind(scrollManager),
    getScrollPosition: (path?: string) => scrollManager.getPosition(path || currentPath)
  };
};

// Export the scroll manager for advanced usage
export const getScrollManager = () => ScrollManager.getInstance(); 